/** Neutral game adapter; legacy recipes keep their exact wrapper and cache identity. */
import { HUSH_HIDEOUT_REVISION } from '../../../../../shared/assets/hushHideout';
import { PIECES, isPieceTemplate, isLegacyPiece, type PieceArt, type PieceTemplate } from '../../../../../shared/assets/pieces';
import { TOWN_PROP_TEMPLATES } from '../../../../../shared/scene/townPropTemplates';
import { TOWN_PROP_PALETTE_KEYS, type TownPropPalette } from '../../../../../shared/scene/townPropTemplates';
import { piecePaletteRoles } from '../../../../../shared/assets/pieces/townPalette';
import { sha256, stableStringify } from '../tidalCascade/rng';

export const LEGACY_ADAPTER_REVISION = 'hush-hideout.adapter/revision-01';
export { isPieceTemplate };
export type { PieceTemplate };
const PIECE_ADAPTER_REVISION = 'pieces.adapter/revision-01';
const revision = (template: string) => isLegacyPiece(template) ? HUSH_HIDEOUT_REVISION : 'pieces/revision-01';
const prefix = (template: string) => isLegacyPiece(template) ? 'hh' : 'piece';

export interface PiecePlacement extends PieceArt {
  readonly template: PieceTemplate;
  readonly recipeId: string;
  readonly logicalRecipeHash: string;
  readonly collisionClaim: 'none';
}

const memo = new Map<string, PiecePlacement>();

export function describePiece(template: PieceTemplate, palette: TownPropPalette): PiecePlacement {
  if (!isPieceTemplate(template)) throw new Error(`Unknown hush hideout template ${JSON.stringify(template)}`);
  const piece = PIECES[template];
  const roles = piecePaletteRoles(piece.recipe.paletteId, palette);
  const logicalRecipeHash = sha256(stableStringify({ recipe: piece.recipe, roles, revision: revision(template) }));
  const key = template + logicalRecipeHash;
  const hit = memo.get(key);
  if (hit) return hit;
  const art = piece.build(roles, `${prefix(template)}_${template}_${logicalRecipeHash.slice(0, 12)}`);
  const catalog = TOWN_PROP_TEMPLATES[template].frame;
  if (catalog.w !== art.frame.w || catalog.h !== art.frame.h) throw new Error(`hush hideout ${template}: catalog frame drifted from its generator`);
  const placed: PiecePlacement = { ...art, template, recipeId: piece.recipe.id, logicalRecipeHash, collisionClaim: 'none' };
  if (memo.size >= 48) memo.delete(memo.keys().next().value as string);
  memo.set(key, placed);
  return placed;
}

export function buildPieceBody(template: PieceTemplate, palette: TownPropPalette): string {
  const placed = describePiece(template, palette);
  return `<g data-part="${isLegacyPiece(template) ? 'hush-hideout' : 'piece'}-${template.replace(/_/g, '-')}" data-recipe="${placed.recipeId}" data-integration="GENERATED_ASSET">${placed.body}</g>`;
}

/** `__tpl_<template>_<10 palette hexes>_<16-hex content digest>`. */
export function pieceTextureSuffix(template: PieceTemplate, palette: TownPropPalette): string {
  const { logicalRecipeHash, frame } = describePiece(template, palette);
  const colors = TOWN_PROP_PALETTE_KEYS.map(k => palette[k].toString(16).padStart(6, '0')).join('-');
  const digest = sha256(stableStringify({ template, logicalRecipeHash, frame, adapter: isLegacyPiece(template) ? LEGACY_ADAPTER_REVISION : PIECE_ADAPTER_REVISION })).slice(0, 16);
  return `__tpl_${template}_${colors}_${digest}`;
}
